/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { createSelector, createSlice } from '@reduxjs/toolkit';
import { ThunkStatus } from '../../../enums/ThunkStatus';
import { editOrganization, getOrganizationById, saveOrganization } from './organization.editor.thunk';

/* Initial State */
const initialState = {
  resetOrg: false,
  status: ThunkStatus.IDLE,
  organization: {},
  error: undefined,
};

/* Slice */
export const organizationEditorSlice = createSlice({
  name: 'organizationEditor',
  initialState,
  reducers: {
    updateOrganization(state, action) {
      state.organization = action.payload;
    },
    resetOrganization(state) {
      state.status = ThunkStatus.IDLE;
      state.organization = {};
      state.error = undefined;
      state.resetOrg = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getOrganizationById.pending, (state) => {
        state.status = ThunkStatus.LOADING;
      })
      .addCase(getOrganizationById.fulfilled, (state, action) => {
        state.status = ThunkStatus.SUCCEEDED;
        state.organization = action.payload;
        state.error = undefined;
      })
      .addCase(getOrganizationById.rejected, (state, action) => {
        state.status = ThunkStatus.FAILED;
        state.error = action?.error?.message;
      })
      .addCase(saveOrganization.pending, (state) => {
        state.status = ThunkStatus.LOADING;
      })
      .addCase(saveOrganization.fulfilled, (state, action) => {
        state.status = ThunkStatus.SUCCEEDED;
        state.organization = action.payload;
        state.resetOrg = true;
        state.error = undefined;
      })
      .addCase(saveOrganization.rejected, (state, action) => {
        state.status = ThunkStatus.FAILED;
        state.error = action?.error?.message;
      })
      .addCase(editOrganization.pending, (state) => {
        state.status = ThunkStatus.LOADING;
      })
      .addCase(editOrganization.fulfilled, (state) => {
        state.status = ThunkStatus.SUCCEEDED;
        state.resetOrg = true;
        state.error = undefined;
      })
      .addCase(editOrganization.rejected, (state, action) => {
        state.status = ThunkStatus.FAILED;
        state.error = action?.error?.message;
      });
  },
});

/* Selectors */
const selectOrganizationEditor = state => state[organizationEditorSlice.name];
export const selectOrganization = createSelector(selectOrganizationEditor, s => s.organization);
export const selectStatus = createSelector(selectOrganizationEditor, s => s.status);
export const selectResetOrg = createSelector(selectOrganizationEditor, s => s.resetOrg);

/* Actions */
export const { updateOrganization, resetOrganization } = organizationEditorSlice.actions;
